// WhatsApp Flows: the Meta-hosted forms a contact fills in inside the chat.
// Route: /settings/flows

import { useEffect, useState } from "react";

import { http, qs } from "../api";
import { useSession } from "../App";
import {
  Badge,
  Confirm,
  Dialog,
  DialogFooter,
  Empty,
  Field,
  Pager,
  RelativeTime,
  Search,
  Spinner,
  Table,
  Tag,
  useAsync,
  useToast,
  type Column,
} from "../ui";
import {
  PAGE_SIZE,
  errMessage,
  ErrorBanner,
  useDebounced,
  useAccounts,
} from "./shared";

// ============================================================ whatsapp flows

interface WhatsAppFlow {
  id: string;
  whatsapp_account: string;
  meta_flow_id: string;
  name: string;
  status: string;
  category: string;
  json_version: string;
  flow_json: Record<string, unknown>;
  screens: unknown[];
  preview_url?: string;
  has_local_changes: boolean;
  created_at: string;
  updated_at: string;
}

interface FlowListPayload {
  flows?: WhatsAppFlow[];
  total?: number;
}

const FLOW_CATEGORIES: { value: string; label: string }[] = [
  { value: "SIGN_UP", label: "Sign up" },
  { value: "SIGN_IN", label: "Sign in" },
  { value: "APPOINTMENT_BOOKING", label: "Appointment booking" },
  { value: "LEAD_GENERATION", label: "Lead generation" },
  { value: "CONTACT_US", label: "Contact us" },
  { value: "CUSTOMER_SUPPORT", label: "Customer support" },
  { value: "SURVEY", label: "Survey" },
  { value: "OTHER", label: "Other" },
];

/** Component types Meta rejects an `id` on. */
const COMPONENTS_WITHOUT_ID = new Set([
  "TextHeading",
  "TextSubheading",
  "TextBody",
  "TextInput",
  "TextArea",
  "Dropdown",
  "RadioButtonsGroup",
  "CheckboxGroup",
  "DatePicker",
  "Image",
  "Footer",
]);

interface FlowScreen {
  id?: string;
  title?: string;
  data?: Record<string, unknown>;
  layout?: { type?: string; children?: Record<string, unknown>[] };
}

/** Strip the ids Meta rejects and normalise the layout, exactly as Meta expects it. */
function sanitizeScreensForMeta(screens: unknown[]): FlowScreen[] {
  return screens.map((raw) => {
    const screen = (raw ?? {}) as FlowScreen;
    const children = screen.layout?.children ?? [];
    return {
      id: screen.id,
      title: screen.title,
      data: screen.data ?? {},
      layout: {
        type: screen.layout?.type || "SingleColumnLayout",
        children: children.map((component) => {
          if (!COMPONENTS_WITHOUT_ID.has(String(component.type))) return component;
          const copy = { ...component };
          delete copy.id;
          return copy;
        }),
      },
    };
  });
}

const FLOW_ACCOUNT_KEY = "flows_selected_account";

export function FlowsView() {
  const { can } = useSession();
  const toast = useToast();
  const canWrite = can("settings.general", "write");
  const canDelete = can("settings.general", "delete");

  const accounts = useAccounts();
  const [account, setAccount] = useState<string>(
    () => localStorage.getItem(FLOW_ACCOUNT_KEY) || "all"
  );
  const [search, setSearch] = useState("");
  const query = useDebounced(search);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [query, account]);

  useEffect(() => {
    if (!accounts.length) return;
    if (account !== "all" && !accounts.some((a) => a.name === account)) {
      setAccount("all");
      localStorage.setItem(FLOW_ACCOUNT_KEY, "all");
    }
  }, [accounts, account]);

  const { data, error, loading, reload } = useAsync<FlowListPayload>(
    () =>
      http.get<FlowListPayload>(
        `/flows${qs({
          account: account === "all" ? "" : account,
          search: query,
          page,
          limit: PAGE_SIZE,
        })}`
      ),
    [account, query, page]
  );
  const flows = data?.flows ?? [];
  const total = data?.total ?? flows.length;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<WhatsAppFlow | null>(null);
  const [form, setForm] = useState({
    whatsapp_account: "",
    name: "",
    category: "",
    json_version: "6.0",
  });
  const [screensText, setScreensText] = useState("[]");
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<WhatsAppFlow | null>(null);

  const chooseAccount = (value: string) => {
    setAccount(value);
    localStorage.setItem(FLOW_ACCOUNT_KEY, value);
  };

  const openCreate = () => {
    setEditing(null);
    setForm({
      whatsapp_account: account !== "all" ? account : accounts[0]?.name ?? "",
      name: "",
      category: "",
      json_version: "6.0",
    });
    setScreensText("[]");
    setDialogOpen(true);
  };

  const openEdit = (flow: WhatsAppFlow) => {
    setEditing(flow);
    setForm({
      whatsapp_account: flow.whatsapp_account,
      name: flow.name,
      category: flow.category || "",
      json_version: flow.json_version || "6.0",
    });
    setScreensText(JSON.stringify(flow.screens ?? [], null, 2));
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name) {
      toast.push("Enter a flow name", "critical");
      return;
    }
    if (!editing && !form.whatsapp_account) {
      toast.push("Select the WhatsApp account this flow belongs to", "critical");
      return;
    }

    let screens: unknown[] = [];
    try {
      const parsed: unknown = JSON.parse(screensText || "[]");
      if (!Array.isArray(parsed)) throw new Error("screens must be a JSON array");
      screens = parsed;
    } catch (err) {
      toast.push(errMessage(err, "The screen JSON is not valid"), "critical");
      return;
    }

    const payload: Record<string, unknown> = {
      name: form.name,
      category: form.category || undefined,
      json_version: form.json_version,
    };
    if (!editing) payload.whatsapp_account = form.whatsapp_account;
    if (screens.length) {
      const sanitized = sanitizeScreensForMeta(screens);
      // Both keys are sent: `flow_json` is what Meta receives, `screens` is what
      // the console reads back.
      payload.flow_json = { version: form.json_version, screens: sanitized };
      payload.screens = sanitized;
    }

    setSaving(true);
    try {
      if (editing) {
        await http.put(`/flows/${editing.id}`, payload);
        toast.push("Flow updated", "good");
      } else {
        await http.post("/flows", payload);
        toast.push("Flow created", "good");
      }
      setDialogOpen(false);
      reload();
    } catch (err) {
      toast.push(errMessage(err, "Could not save the flow"), "critical");
    } finally {
      setSaving(false);
    }
  };

  const runAction = async (flow: WhatsAppFlow, path: string, label: string, failure: string) => {
    setBusyId(flow.id);
    try {
      await http.post(`/flows/${flow.id}/${path}`);
      toast.push(label, "good");
      reload();
    } catch (err) {
      toast.push(errMessage(err, failure), "critical");
    } finally {
      setBusyId(null);
    }
  };

  const sync = async () => {
    if (account === "all") {
      toast.push("Choose a single account before syncing", "critical");
      return;
    }
    setSyncing(true);
    try {
      const res = await http.post<{ synced?: number; created?: number; updated?: number }>(
        "/flows/sync",
        { whatsapp_account: account }
      );
      toast.push(
        `Synced ${res.synced ?? 0} flows (${res.created ?? 0} new, ${res.updated ?? 0} updated)`,
        "good"
      );
      reload();
    } catch (err) {
      toast.push(errMessage(err, "Could not sync from Meta"), "critical");
    } finally {
      setSyncing(false);
    }
  };

  const remove = async () => {
    if (!toDelete) return;
    try {
      await http.del(`/flows/${toDelete.id}`);
      toast.push("Flow deleted", "good");
      setToDelete(null);
      reload();
    } catch (err) {
      toast.push(errMessage(err, "Could not delete the flow"), "critical");
      throw err;
    }
  };

  const columns: Column<WhatsAppFlow>[] = [
    {
      key: "name",
      header: "Name",
      sortable: true,
      render: (f) => (
        <div>
          <div className="strong">{f.name}</div>
          <div className="muted">{f.whatsapp_account}</div>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (f) => {
        const status = (f.status || "").toUpperCase();
        if (status === "DEPRECATED") return <Badge tone="critical">Deprecated</Badge>;
        return <Badge tone={status === "PUBLISHED" ? "good" : "neutral"}>{f.status || "—"}</Badge>;
      },
    },
    {
      key: "category",
      header: "Category",
      sortable: true,
      render: (f) => (f.category ? <Badge tone="neutral">{f.category}</Badge> : <span className="muted">—</span>),
    },
    {
      key: "created_at",
      header: "Created",
      sortable: true,
      render: (f) => <RelativeTime value={f.created_at} />,
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (f) => {
        const status = (f.status || "").toUpperCase();
        return (
          <div className="row" style={{ justifyContent: "flex-end" }}>
            <button
              className="btn icon sm ghost"
              title="Edit"
              disabled={!canWrite}
              onClick={() => openEdit(f)}
            >
              ✎
            </button>
            <button
              className="btn icon sm ghost"
              title="Duplicate"
              disabled={busyId === f.id || !canWrite}
              onClick={() => void runAction(f, "duplicate", "Flow duplicated", "Could not duplicate the flow")}
            >
              {busyId === f.id ? <Spinner label="Working" /> : "⧉"}
            </button>
            {f.preview_url && (
              <a
                className="btn icon sm ghost"
                title="Preview on Meta"
                href={f.preview_url}
                target="_blank"
                rel="noreferrer"
              >
                ↗
              </a>
            )}
            {status !== "DEPRECATED" && (f.has_local_changes || !f.meta_flow_id) && (
              <button
                className="btn icon sm ghost"
                title={f.meta_flow_id ? "Update on Meta" : "Save to Meta"}
                disabled={busyId === f.id || !canWrite}
                onClick={() =>
                  void runAction(f, "save-to-meta", "Flow saved to Meta", "Could not save the flow to Meta")
                }
              >
                ⭱
              </button>
            )}
            {status === "DRAFT" && f.meta_flow_id && (
              <button
                className="btn icon sm ghost"
                title="Publish"
                disabled={busyId === f.id || !canWrite}
                onClick={() => void runAction(f, "publish", "Flow published", "Could not publish the flow")}
              >
                ▷
              </button>
            )}
            <button
              className="btn icon sm ghost"
              title={status === "PUBLISHED" ? "A published flow cannot be deleted" : "Delete"}
              disabled={status === "PUBLISHED" || !canDelete}
              onClick={() => setToDelete(f)}
            >
              ✕
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>WhatsApp Flows</h1>
          <div className="sub">Meta-hosted forms a contact fills in inside the chat.</div>
        </div>
        <div className="page-actions">
          <button className="btn" onClick={() => void sync()} disabled={syncing || account === "all"}>
            {syncing ? <Spinner label="Syncing" /> : "⟳"} Sync from Meta
          </button>
          <button className="btn primary" onClick={openCreate} disabled={!canWrite}>
            + Create flow
          </button>
        </div>
      </div>

      <ErrorBanner error={error} what="Could not load the flows" />

      <div className="card flush">
        <div className="card-head">
          <h3>Your flows</h3>
          <select
            value={account}
            aria-label="Filter by account"
            style={{ width: 180 }}
            onChange={(e) => chooseAccount(e.target.value)}
          >
            <option value="all">All accounts</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.name}>
                {a.name}
              </option>
            ))}
          </select>
          <Search value={search} onChange={setSearch} placeholder="Search flows…" />
        </div>
        <div className="card-body">
          <Table
            columns={columns}
            rows={flows}
            loading={loading}
            empty={
              <Empty
                title={query ? "No matching flows" : "No flows yet"}
                body={
                  query
                    ? `Nothing matches "${query}".`
                    : "Create a flow here, or pull the ones you already have in Meta."
                }
                icon="⌥"
                action={
                  !query && canWrite ? (
                    <button className="btn primary sm" onClick={openCreate}>
                      + Create flow
                    </button>
                  ) : undefined
                }
              />
            }
          />
        </div>
        <div className="card-foot">
          <Pager
            total={total}
            limit={PAGE_SIZE}
            offset={(page - 1) * PAGE_SIZE}
            itemName="flows"
            onChange={(offset) => setPage(Math.floor(offset / PAGE_SIZE) + 1)}
          />
        </div>
      </div>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editing ? "Edit flow" : "Create flow"}
        full
      >
        <div className="grid c3">
          <Field label="WhatsApp account" required={!editing}>
            {editing ? (
              <input value={form.whatsapp_account} disabled readOnly />
            ) : (
              <select
                value={form.whatsapp_account}
                onChange={(e) => setForm({ ...form, whatsapp_account: e.target.value })}
              >
                <option value="">Select an account…</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.name}>
                    {a.name}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Name" required>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field label="Category">
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
            >
              <option value="">Select a category…</option>
              {FLOW_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {editing?.meta_flow_id && (
          <div className="chips">
            <Tag label={`Meta ID: ${editing.meta_flow_id}`} />
            {editing.has_local_changes && <Badge tone="warning">Unsaved on Meta</Badge>}
          </div>
        )}

        <Field
          label="Screens"
          hint="Meta Flow JSON. Ids Meta rejects are stripped for you when the flow is saved."
        >
          <textarea
            rows={18}
            className="mono"
            value={screensText}
            spellCheck={false}
            onChange={(e) => setScreensText(e.target.value)}
          />
        </Field>

        <DialogFooter>
          <button className="btn" onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </button>
          <button className="btn primary" onClick={() => void save()} disabled={saving}>
            {saving && <Spinner label="Saving" />}
            {editing ? "Save changes" : "Create flow"}
          </button>
        </DialogFooter>
      </Dialog>

      <Confirm
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={() => void remove()}
        title="Delete this flow?"
        body={`"${toDelete?.name ?? ""}" is removed here only. A flow that exists in Meta stays live there and returns on the next sync.`}
        danger
      />
    </div>
  );
}
